// Scheduling-strategy editor-state types. Unlike the other three areas the backend has no
// unified discriminator enum (each strategy model carries its own `type` literal), so we
// declare a local `SchedulingStrategyType` whose string values match the backend.

export const SchedulingStrategyType = {
  AfterEmployeeCreationSchedulingStrategy: 'AfterEmployeeCreationSchedulingStrategy',
  // NB: backend discriminator carries a typo ("Scheudling"); value must match it verbatim.
  DefinedBySubtypeSchedulingStrategy: 'DefinedBySubtypeScheudlingStrategy',
  RepeatPatternSchedulingStrategy: 'RepeatPatternSchedulingStrategy',
  ScheduleDefinitionSchedulingStrategy: 'ScheduleDefinitionSchedulingStrategy',
} as const
export type SchedulingStrategyType = (typeof SchedulingStrategyType)[keyof typeof SchedulingStrategyType]

export interface BaseSchedulingStrategy {
  type: SchedulingStrategyType
}

export function isBaseSchedulingStrategy(value: unknown): value is BaseSchedulingStrategy {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    Object.values(SchedulingStrategyType).includes((value as BaseSchedulingStrategy).type)
  )
}

export const ActionModuleFrequency = {
  Daily: 'Daily',
  Weekly: 'Weekly',
  Monthly: 'Monthly',
  Yearly: 'Yearly',
} as const
export type ActionModuleFrequency = (typeof ActionModuleFrequency)[keyof typeof ActionModuleFrequency]

export interface RepeatPatternSchedulingStrategyModel extends BaseSchedulingStrategy {
  frequency: ActionModuleFrequency
}

export function isRepeatPatternSchedulingStrategyModel(
  value: unknown,
): value is RepeatPatternSchedulingStrategyModel {
  return (
    isBaseSchedulingStrategy(value) &&
    value.type === SchedulingStrategyType.RepeatPatternSchedulingStrategy &&
    'frequency' in value &&
    Object.values(ActionModuleFrequency).includes((value as RepeatPatternSchedulingStrategyModel).frequency)
  )
}

export interface ScheduleDefinitionSchedulingStrategyModel extends BaseSchedulingStrategy {
  scheduleDefinition: ScheduleDefinitionBase
}

export function isScheduleDefinitionSchedulingStrategyModel(
  value: unknown,
): value is ScheduleDefinitionSchedulingStrategyModel {
  return (
    isBaseSchedulingStrategy(value) &&
    value.type === SchedulingStrategyType.ScheduleDefinitionSchedulingStrategy &&
    'scheduleDefinition' in value
  )
}

export const ScheduleDefinitionType = {
  Time: 'Time',
  Day: 'Day',
  Week: 'Week',
  Month: 'Month',
  Quarter: 'Quarter',
} as const
export type ScheduleDefinitionType = (typeof ScheduleDefinitionType)[keyof typeof ScheduleDefinitionType]

export interface ScheduleDefinitionBase {
  id?: number
  discriminator: ScheduleDefinitionType
  startDate: string | null
  endDate: string | null
  combineConsecutiveDays: boolean
  separateByWeek: boolean
  separateByMonth: boolean
}

export const DayOfWeek = {
  Monday: 'Monday',
  Tuesday: 'Tuesday',
  Wednesday: 'Wednesday',
  Thursday: 'Thursday',
  Friday: 'Friday',
  Saturday: 'Saturday',
  Sunday: 'Sunday',
} as const
export type DayOfWeek = (typeof DayOfWeek)[keyof typeof DayOfWeek]

// Times are kept as `HH:mm` strings (native <input type="time">), not dayjs objects.
export interface ScheduleDay {
  dayOfWeek: DayOfWeek
  startTime: string | null
  endTime: string | null
}

export const ScheduleWeekMode = { None: 'None', Repeat: 'Repeat', Skip: 'Skip' } as const
export type ScheduleWeekMode = (typeof ScheduleWeekMode)[keyof typeof ScheduleWeekMode]

export interface ScheduleWeek {
  mode: ScheduleWeekMode
  value: number
  days?: ScheduleDay[]
}

export const ScheduleMonthMode = { None: 'None', Repeat: 'Repeat', Skip: 'Skip' } as const
export type ScheduleMonthMode = (typeof ScheduleMonthMode)[keyof typeof ScheduleMonthMode]

export interface ScheduleMonth {
  mode: ScheduleMonthMode
  value: number
  weekOffsetInsideMonth: number
  week?: ScheduleWeek
}

export const ScheduleQuarterMode = { None: 'None', Repeat: 'Repeat', Skip: 'Skip' } as const
export type ScheduleQuarterMode = (typeof ScheduleQuarterMode)[keyof typeof ScheduleQuarterMode]

export interface ScheduleQuarter {
  mode: ScheduleQuarterMode
  value: number
  monthOffsetInsideQuarter: number
  month?: ScheduleMonth
}

export interface ScheduleTimePeriod {
  startTime: string | null
  endTime: string | null
}

export interface ScheduleDaysDefinition extends ScheduleDefinitionBase {
  days: ScheduleDay[]
}
export interface ScheduleWeekDefinition extends ScheduleDefinitionBase {
  week: ScheduleWeek
}
export interface ScheduleMonthDefinition extends ScheduleDefinitionBase {
  month: ScheduleMonth
}
export interface ScheduleQuarterDefinition extends ScheduleDefinitionBase {
  quarter: ScheduleQuarter
}
export interface ScheduleTimePeriodsDefinition extends ScheduleDefinitionBase {
  timePeriods: ScheduleTimePeriod[]
}

export type EditorSchedulingStrategy = Omit<BaseSchedulingStrategy, 'type'>

export interface SchedulingStrategyEditorProps {
  value: EditorSchedulingStrategy | null
  onChanged: (value: BaseSchedulingStrategy) => void
}
